import { log } from "console";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

interface ProfileProps {
  updateUserName: (newUserName: string) => void;
}

interface UserProfile {
  id: number;
  userName: string;
  email: string;
  phoneNumber: string;
  azureToken: string;
  roleId: number;
  roleName: string;
  createdDate: string;
  updatedDate: string;
  status: string | null;
}

const Profile: React.FC<ProfileProps> = ({ updateUserName }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileData, setProfileData] = useState({
    userName: '',
    email: '',
    phoneNumber: ''
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleUpdateClick = () => {
    navigate('/update-user-profile', {
      state: { userProfile: { userName: profileData.userName, email: profileData.email, phoneNumber: profileData.phoneNumber } }
    });
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };


  useEffect(() => {
    
    if (profileData.phoneNumber) {
      const phoneLength = profileData.phoneNumber.length;
      if (phoneLength > 10) {
        setCountryCode(profileData.phoneNumber.slice(0, phoneLength - 10)); // Extract Country Code
        setPhoneNumber(profileData.phoneNumber.slice(-10)); // Extract Last 10 Digits
      } else {
        setCountryCode("+"); // Fallback if no country code
        setPhoneNumber(profileData.phoneNumber);
      }
    }
  }, [profileData.phoneNumber]);


  useEffect(() => {
    // Try to get data from location state first
    if (location.state?.userName) {
      setProfileData({
        userName: location.state.userName,
        email: location.state.email,
        phoneNumber: location.state.phoneNumber
      });
    } else {
      // If no location state, fetch from API
      const fetchProfile = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error('Session expired. Please log in again.');
          navigate('/');
          return;
        }

        try {
          const response = await fetch('https://pamsapi.vofoxsolutions.com/api/User/GetUserProfile', {
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) throw new Error('Failed to fetch profile');

          const data = await response.json();
          setProfileData({
            userName: data.userName,
            email: data.email,
            phoneNumber: data.phoneNumber
          });
        } catch (error) {
          toast.error("Failed to load profile data");
        }
      };

      fetchProfile();
    }
  }, [location.state, navigate]);

  const fetchUserProfile = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage('Session expired. Please log in again.');
      navigate('/home');
      return;
    }

    try {
      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/User', {
        method: 'GET',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user profile: ${response.status} ${response.statusText}`);
      }

      const data: UserProfile = await response.json();
      setUserProfile(data);
      setProfileData({
        userName: data.userName,
        email: data.email,
        phoneNumber: data.phoneNumber
      });
      updateUserName(data.userName);

      setIsProfileModalOpen(false);
      navigate('/profile', {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      toast.error("An error occurred while fetching user profile.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    sessionStorage.clear();
    navigate('/');
  };
  const handleProfile = () => {
    navigate('/profile')
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 overflow-hidden">
      <header className="flex items-center justify-between px-4 py-3 bg-white shadow md:px-6">
        <div>
          {/* <h1 className="text-base font-semibold text-gray-700 md:text-lg">Postal Address Mapping</h1> */}
        </div>
        <div className="flex items-center gap-4 md:gap-6">
          <div className="relative m-2 md:m-3">
            <button
              onClick={toggleDropdown}
              className="relative border border-whitesmoke focus:outline-none flex rounded-full h-10 md:h-12 items-center px-2 gap-2"
            >
              <div className="w-7 h-7 md:w-8 md:h-8 overflow-hidden rounded-full">
                <img
                  className="object-cover w-full h-full"
                  src="https://cdn-icons-png.flaticon.com/128/18573/18573514.png"
                  alt="User Avatar"
                />
              </div>
              <h1 className="text-sm md:text-base">{profileData.userName || 'Guest'}</h1>
            </button>
            {dropdownOpen && (
              <>
                <div onClick={() => setDropdownOpen(false)} className="fixed inset-0 z-10 w-full h-full"></div>
                <div className="absolute right-0 z-20 w-40 md:w-48 mt-2 bg-white rounded-md shadow-xl">
                  <button
                    onClick={handleProfile}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                  >
                    Profile
                  </button>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                  >
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
      <div className="p-4 md:p-6 flex-grow">
        <h1 className="text-xl font-bold mb-4 md:mb-6 md:text-2xl">My Profile</h1>
        <div className="w-full p-6 bg-white rounded-lg shadow-md">
          <form className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                id="username"
                type="text"
                value={profileData.userName}
                className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                readOnly
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={profileData.email}
                className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                readOnly
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                id="phone"
                type="text"
                value={profileData.phoneNumber}
                className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                readOnly
              />
            </div>
            <div className="col-span-1 sm:col-span-2 lg:col-span-3 flex items-center justify-end gap-4 mt-4">
              <button
                onClick={handleUpdateClick}
                className="py-2 px-4 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Edit Profile
              </button>
              <button
                onClick={() => navigate('/home')}
                className="py-2 px-4 bg-gray-300 text-gray-800 rounded-md shadow hover:bg-gray-400 focus:outline-none"
              >
                Close
              </button>
            </div>
          </form>
        </div>
      </div>
      <footer className="w-full mt-8 md:mt-16">
        <div className="px-4 py-4 bg-white shadow rounded-md md:px-6 md:py-6">
          <p className="text-center text-sm text-gray-500">GET ME THERE. © 2024</p>
        </div>
      </footer>
    </div>


  );
};

// Add this skeleton loader component
const ProfileSkeleton = () => (
  <div className="w-full max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md animate-pulse">
    {/* Profile Header */}
    <div className="flex items-center justify-center mb-8">
      <div className="w-24 h-24 bg-gray-200 rounded-full"></div>
    </div>

    {/* Profile Fields */}
    <div className="space-y-6">
      <div>
        <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
        <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>
      <div>
        <div className="h-4 w-16 bg-gray-200 rounded mb-2"></div>
        <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>
      <div>
        <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
        <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>
    </div>

    {/* Action Button */}
    <div className="flex justify-end mt-8">
      <div className="h-10 w-32 bg-gray-200 rounded"></div>
    </div>
  </div>
);

export default Profile;
